import React from 'react';
import { motion } from 'framer-motion';
import { Globe, MapPin, TrendingUp, Users } from 'lucide-react';

function MarketSection({ content }) {
  const stats = [
    { value: '650K+', label: 'Licensed professionals nationwide', icon: Users },
    { value: '444K+', label: 'Beauty establishments', icon: MapPin },
    { value: '35-45K', label: 'Professionals in NYC metro', icon: Globe },
    { value: '72%', label: 'Salons using booking apps', icon: TrendingUp },
  ];

  return (
    <section className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-20">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-mint/10 border border-accent-mint/20 rounded-full mb-6">
            <TrendingUp className="w-4 h-4 text-accent-mint" />
            <span className="text-sm text-accent-mint">Market Opportunity</span>
          </div>
          <h2 className="section-title mb-4">
            Massive & <span className="text-gradient">Underserved</span>
          </h2>
          <p className="section-subtitle mx-auto">
            The US beauty services market is larger than the movie, music, and gaming industries combined.
          </p>
        </motion.div>

        {/* TAM / SAM / SOM */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card text-center hover-lift"
          >
            <div className="text-sm text-zinc-500 mb-2 uppercase tracking-wider">Total Addressable Market</div>
            <div className="text-5xl md:text-6xl font-bold text-gradient mb-2">$75B+</div>
            <div className="text-sm text-zinc-400">US Professional Beauty Services</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card text-center hover-lift border-kore-500/30"
          >
            <div className="text-sm text-zinc-500 mb-2 uppercase tracking-wider">Serviceable Market</div>
            <div className="text-5xl md:text-6xl font-bold text-kore-400 mb-2">$3-4B</div>
            <div className="text-sm text-zinc-400">NYC Metro Target Market</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="card text-center hover-lift"
          >
            <div className="text-sm text-zinc-500 mb-2 uppercase tracking-wider">Obtainable Market</div>
            <div className="text-5xl md:text-6xl font-bold text-accent-mint mb-2">$15M</div>
            <div className="text-sm text-zinc-400">Year 3 Revenue Target</div>
          </motion.div>
        </div>

        {/* Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12"
        >
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="bg-surface-card border border-surface-border rounded-xl p-4 text-center"
              >
                <Icon className="w-5 h-5 text-kore-400 mx-auto mb-2" />
                <div className="text-2xl font-bold text-white">{stat.value}</div>
                <div className="text-xs text-zinc-500">{stat.label}</div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Launch Strategy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="card bg-gradient-to-r from-surface-card to-kore-900/20"
        >
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <MapPin className="w-12 h-12 text-kore-400" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-semibold mb-2">Launch Strategy: NYC Metro</h3>
              <p className="text-zinc-400 text-sm">
                Concentrated launch in NY, NJ, and CT. The density strategy ensures network effects and marketplace liquidity before geographic expansion.
              </p>
            </div>
            <div className="flex gap-4">
              <div className="text-center">
                <div className="text-lg font-bold text-white">NY</div>
                <div className="text-xs text-zinc-500">Primary</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-zinc-400">NJ</div>
                <div className="text-xs text-zinc-500">Secondary</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-zinc-400">CT</div>
                <div className="text-xs text-zinc-500">Secondary</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default MarketSection;
